import { PRINTOUT_TEMPLATE_PROBLEMS } from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { paginated, paginationInputSchema } from './common/pagination.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

export const GENERATED_FILE_KINDS = [
  'donation_export',
  'printout_template_preview',
  'reconciliation_shortfall_export',
  'refund_export',
  'fund_transfer_export',
  'donation_printout',
  'queue_sheet',
  'donation_totals_export',
  'campaign_performance_export',
  'donor_segment_export',
  'season_comparison_export',
  'user_commitment_export',
  'accrual_export',
  'audit_log_export',
  'donor_data_export',
  'queue_export',
  'reconciliation_history_export',
  'donation_import_template',
  'bulk_user_template',
  'bulk_institution_template',
  'bulk_donation_template',
] as const;

export type GeneratedFileKind = (typeof GENERATED_FILE_KINDS)[number];

export const GENERATED_FILE_STATUSES = ['pending', 'ready', 'failed', 'expired'] as const;

export type GeneratedFileStatus = (typeof GENERATED_FILE_STATUSES)[number];

/**
 * How long a file's rows are kept before `generated-files.expire` deletes the
 * object — .claude/rules/file-uploads.md *Generated files*. It lives in
 * `@btsis/core` beside every other period, and is re-exported here because the
 * screens and the build service have always read it from the contract.
 */
export { GENERATED_FILE_RETENTION_HOURS } from '@btsis/core';

/** A link lives a minute, and every one issued is an audit row. */
export const GENERATED_FILE_DOWNLOAD_SECONDS = 60;

/**
 * The most rows one generated file may hold. Past it the job refuses rather than
 * truncating: a spreadsheet that silently stops at the cap is read as the whole
 * answer, which is the failure mode an export exists to avoid. The row carries
 * the count it found, so the screen can say by how much to narrow the filter —
 * docs/plans/01-foundation.md §1.9.
 */
export const GENERATED_FILE_MAX_ROWS = 100_000;

/**
 * Why a file has no bytes. `GENERATED_FILE_REQUESTER_REFUSED` is the requester
 * re-authorized at generation time and found wanting — suspended, re-roled, or
 * moved out of the scope the filter reaches.
 */
export const PRINTOUT_TEMPLATE_RENDER_FAILURE_CODES = [
  ...PRINTOUT_TEMPLATE_PROBLEMS,
  'PRINTOUT_TEMPLATE_MEDIA_INVALID',
  'PRINTOUT_TEMPLATE_MEDIA_UNAVAILABLE',
] as const;
export type PrintoutTemplateRenderFailureCode =
  (typeof PRINTOUT_TEMPLATE_RENDER_FAILURE_CODES)[number];

export const GENERATED_FILE_FAILURE_CODES = [
  'GENERATED_FILE_TOO_MANY_ROWS',
  'GENERATED_FILE_REQUESTER_REFUSED',
  'GENERATED_FILE_BUILD_FAILED',
  ...PRINTOUT_TEMPLATE_RENDER_FAILURE_CODES,
] as const;

export type GeneratedFileFailureCode = (typeof GENERATED_FILE_FAILURE_CODES)[number];

/**
 * A file the worker wrote from the database, as its requester reads it. The
 * request parameters are **not** returned: they are on the row so the job reads
 * the filter from a place nobody outside the transaction wrote, not so a screen
 * can echo them back.
 */
export const generatedFileSchema = z.object({
  id: z.uuid(),
  kind: z.enum(GENERATED_FILE_KINDS),
  status: z.enum(GENERATED_FILE_STATUSES),
  requestedAt: z.string(),
  fileName: z.string().nullable(),
  contentType: z.string().nullable(),
  fileSize: z.number().int().nullable(),
  rowCount: z.number().int().nullable(),
  completedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  failureCode: z.enum(GENERATED_FILE_FAILURE_CODES).nullable(),
});

export type GeneratedFile = z.infer<typeof generatedFileSchema>;

export const generatedFileListInputSchema = paginationInputSchema.extend({
  kind: z.enum(GENERATED_FILE_KINDS).optional(),
  status: z.enum(GENERATED_FILE_STATUSES).optional(),
});

export type GeneratedFileListInput = z.infer<typeof generatedFileListInputSchema>;

export const generatedFileDownloadSchema = z.object({
  url: z.string(),
  fileName: z.string(),
  expiresInSeconds: z.number().int(),
});

export type GeneratedFileDownload = z.infer<typeof generatedFileDownloadSchema>;

/**
 * Files a person asked the worker for — docs/decisions/0043-the-worker.md §7.
 * Every route here reads **the caller's own** files and nothing else: the
 * requester is the whole authorization, so there is no permission and no scope
 * predicate, and another person's file is `NOT_FOUND`. What each kind of file
 * may be asked for is decided by the module that owns the list being exported,
 * which is where the permission sits.
 */
export const generatedFileContract = {
  list: oc
    .route({ method: 'GET', path: '/generated-files', summary: 'Files the caller asked for' })
    .errors(guarded)
    .input(generatedFileListInputSchema)
    .output(paginated(generatedFileSchema)),

  /** One file, for a screen waiting on a `pending` one to become `ready`. */
  get: oc
    .route({ method: 'GET', path: '/generated-files/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(generatedFileSchema),

  /** A 60-second presigned link for the requester alone; every call writes an audit row. */
  download: oc
    .route({ method: 'GET', path: '/generated-files/{id}/download' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(generatedFileDownloadSchema),
};
